import { z } from 'zod';

/**
 * Wire schemas for list API responses
 * These match the backend response format (snake_case)
 *
 * IMPORTANT: Backend stores:
 * - Timestamps as ISO string DATETIME, not Unix numbers
 * - Optional fields can be null, not just undefined
 */

// ============================
// Enums
// ============================

export const listTypeSchema = z.enum(['default', 'custom']);
export const listVisibilitySchema = z.enum(['private', 'friends', 'public']);
export const listItemTypeSchema = z.enum(['menu_item', 'business']);
export const listMemberRoleSchema = z.enum(['editor', 'viewer']);
export const listMemberStatusSchema = z.enum(['pending', 'accepted']);

// ============================
// Read Schemas (API responses)
// ============================

/**
 * List member wire schema (matches backend ListMemberReadSchema)
 * Defined before listWireSchema because it is referenced as an optional field.
 */
export const listMemberWireSchema = z.object({
  id: z.string(),
  list_id: z.string(),
  user_id: z.string(),
  role: listMemberRoleSchema,
  status: listMemberStatusSchema,
  invited_by: z.string(),
  // Optional populated fields from JOINs
  user_name: z.string().optional(),
  user_username: z.string().optional(),
  user_image: z.string().nullable().optional(),
  created_at: z.string(), // D1 DATETIME (ISO format)
  updated_at: z.string(), // D1 DATETIME (ISO format)
});

export type ListMemberWire = z.infer<typeof listMemberWireSchema>;

/**
 * List wire schema (matches backend ListReadSchema)
 */
export const listWireSchema = z.object({
  id: z.string(),
  owner_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  type: listTypeSchema,
  visibility: listVisibilitySchema,
  cover_image_url: z.string().nullable(),
  item_count: z.number().int().min(0),
  member_count: z.number().int().min(0),
  // Optional populated fields from JOINs
  owner_name: z.string().optional(),
  created_at: z.string(), // D1 DATETIME (ISO format)
  updated_at: z.string(), // D1 DATETIME (ISO format)
  // Present when user has a pending invitation to this list
  pending_membership: listMemberWireSchema.optional(),
});

export type ListWire = z.infer<typeof listWireSchema>;

/**
 * List item wire schema (matches backend ListItemReadSchema)
 */
export const listItemWireSchema = z.object({
  id: z.string(),
  list_id: z.string(),
  item_type: listItemTypeSchema,
  item_id: z.string(),
  note: z.string().nullable(),
  added_by: z.string(),
  // Optional populated fields from JOINs
  added_by_name: z.string().optional(),
  business_name: z.string().optional(),
  menu_item_name: z.string().optional(),
  cover_photo_url: z.string().nullable().optional(),
  created_at: z.string(), // D1 DATETIME (ISO format)
});

export type ListItemWire = z.infer<typeof listItemWireSchema>;

// ============================
// Paginated Response Schemas
// ============================

/**
 * Paginated list response schema
 * Matches createPaginatedResponseSchema(ListReadSchema) from Gateway
 */
export const paginatedListResponseSchema = z.object({
  items: z.array(listWireSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  per_page: z.number().int().min(1).max(100),
  total_pages: z.number().int().min(0),
});

export type PaginatedListResponseWire = z.infer<typeof paginatedListResponseSchema>;

/**
 * Paginated list item response schema
 * Matches createPaginatedResponseSchema(ListItemReadSchema) from Gateway
 */
export const paginatedListItemResponseSchema = z.object({
  items: z.array(listItemWireSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  per_page: z.number().int().min(1).max(100),
  total_pages: z.number().int().min(0),
});

export type PaginatedListItemResponseWire = z.infer<typeof paginatedListItemResponseSchema>;

// ============================
// Item Status Response
// ============================

/**
 * Item status response schema
 * Returns which lists contain a given item
 */
export const listItemStatusResponseSchema = z.object({
  list_ids: z.array(z.string()),
});

export type ListItemStatusResponseWire = z.infer<typeof listItemStatusResponseSchema>;

// ============================
// Input Schemas (API requests)
// ============================

/**
 * List creation input schema
 */
export const listCreateInputSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
  visibility: listVisibilitySchema.optional(),
});

export type ListCreateInput = z.infer<typeof listCreateInputSchema>;

/**
 * List update input schema
 */
export const listUpdateInputSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less').optional(),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
  visibility: listVisibilitySchema.optional(),
});

export type ListUpdateInput = z.infer<typeof listUpdateInputSchema>;

/**
 * List item creation input schema
 */
export const listItemCreateInputSchema = z.object({
  item_type: listItemTypeSchema,
  item_id: z.string().min(1, 'Item ID is required'),
  note: z.string().max(500, 'Note must be 500 characters or less').optional(),
});

export type ListItemCreateInput = z.infer<typeof listItemCreateInputSchema>;

/**
 * List item update input schema (note only)
 */
export const listItemUpdateInputSchema = z.object({
  note: z.string().max(500, 'Note must be 500 characters or less').optional(),
});

export type ListItemUpdateInput = z.infer<typeof listItemUpdateInputSchema>;

/**
 * List member creation (invite) input schema
 */
export const listMemberCreateInputSchema = z.object({
  user_id: z.string().min(1, 'User ID is required'),
  role: listMemberRoleSchema,
});

export type ListMemberCreateInput = z.infer<typeof listMemberCreateInputSchema>;
